import { Module } from 'vuex';
import { RootState } from '@/interfaces/store';
import {
    MassBalanceState,
    MassBalanceData,
    FormStepData,
    FlowmetersFormData,
    ProductsFormData,
    AnalysisFormData,
    MassBalanceFlowMeterData,
    FlowMeterConfig,
    E4Config,
    MassBalanceConfig,
} from '@/interfaces/massBalanceInterface';
const massBalanceModule: Module<MassBalanceState, RootState> = {
    namespaced: false,
    state: {
        massBalanceData: {
            flowMeters: {
                allNetworks: [],
                data: [],
                summaryData: [],
                selectedNetworks: [],
            },
            formState: {
                step: {
                    name: '',
                    reportUnit: {
                        type: [],
                        massUnit: '',
                        volumeUnit: '',
                        steamProductionUnit: '',
                    },
                    indicatorType: '',
                    injectionControl: '',
                    type: '',
                    selectedNetworkId: '',
                },
                flowmeters: {},
                products: {
                    data: [],
                    summaryData: [],
                },
                analyses: {
                    newData: [],
                    summaryData: [],
                },
            },
        },
        e4Config: null,
        analysis: null,
    },
    getters: {
        massBalanceData: (state) => state.massBalanceData,
        e4Config: (state) => state.e4Config,
        formState: (state) => state.massBalanceData.formState,
        currentFormData: (state) => ({
            step: state.massBalanceData.formState.step,
            flowmeters: state.massBalanceData.formState.flowmeters,
            products: state.massBalanceData.formState.products,
            analyses: state.massBalanceData.formState.analyses,
        }),
    },
    mutations: {
        SET_MASS_BALANCE_DATA(state, data: MassBalanceData) {
            state.massBalanceData = data;
        },
        SET_FLOWMETER_NETWORK(state, payload: MassBalanceFlowMeterData) {
            state.massBalanceData.flowMeters = payload;
        },
        SET_FORM_STEP_DATA(state, stepData: FormStepData) {
            state.massBalanceData.formState.step = { ...stepData };
        },
        SET_FORM_FLOWMETERS_DATA(state, flowmetersData: FlowmetersFormData) {
            const deepCopyFlowmeters: FlowmetersFormData = {};
            Object.keys(flowmetersData).forEach((key) => {
                deepCopyFlowmeters[key] = flowmetersData[key].map((item) => ({
                    networkId: item.networkId,
                    flowmeterIds: [...item.flowmeterIds],
                }));
            });
            state.massBalanceData.formState.flowmeters = deepCopyFlowmeters;
        },
        SET_FORM_PRODUCTS_DATA(state, productsData: ProductsFormData) {
            state.massBalanceData.formState.products = { ...productsData };
        },
        SET_FORM_ANALYSES_DATA(state, analysesData: AnalysisFormData) {
            state.massBalanceData.formState.analyses = { ...analysesData };
        },
        RESET_MASS_BALANCE_DATA(state) {
            state.massBalanceData = {
                flowMeters: {
                    allNetworks: [],
                    data: [],
                    summaryData: [],
                    selectedNetworks: [],
                },
                formState: {
                    step: {
                        name: '',
                        reportUnit: {
                            type: [],
                            massUnit: '',
                            volumeUnit: '',
                            steamProductionUnit: '',
                        },
                        type: '',
                        indicatorType: '',
                        injectionControl: '',
                        selectedNetworkId: '',
                    },
                    flowmeters: {},
                    products: {
                        data: [],
                        summaryData: [],
                    },
                    analyses: {
                        newData: [],
                        summaryData: [],
                    },
                },
            };
            state.e4Config = null;
        },
        POPULATE_FORM_FOR_EDITING(state, massBalanceData: MassBalanceConfig) {
            state.massBalanceData.formState = {
                step: {
                    name: massBalanceData.name || '',
                    reportUnit: massBalanceData.reportUnit || {
                        type: [],
                        massUnit: '',
                        volumeUnit: '',
                        steamProductionUnit: '',
                    },
                    type: massBalanceData.type || '',
                    indicatorType: massBalanceData.indicatorType || '',
                    injectionControl: massBalanceData.injectionControl || '',
                    selectedNetworkId: massBalanceData.selectedNetworkId || '',
                },
                flowmeters: (() => {
                    const flowmeters = massBalanceData.flowmeters || [];
                    const deepCopyFlowmeters: FlowmetersFormData = {};
                    flowmeters.forEach((flowmeter: FlowMeterConfig) => {
                        deepCopyFlowmeters[flowmeter.type.toLowerCase()] = flowmeter.config;
                    });
                    return deepCopyFlowmeters;
                })(),
                products: {
                    data: massBalanceData.products || [],
                    summaryData: [],
                },
                analyses: {
                    newData: massBalanceData.analysis || [],
                    summaryData: [],
                },
            };
            state.massBalanceData.flowMeters.flowmeterGeneralProps =
                massBalanceData.flowmeterGeneralProps;
        },
        SET_E4_CONFIG(state, config: E4Config) {
            state.e4Config = config;
        },
        SET_ANALYSIS(state, analysis: AnalysisFormData) {
            state.massBalanceData.formState.analyses = { ...analysis };
        },
    },
    actions: {
        setFlowmeterNetwork({ commit }, payload: MassBalanceFlowMeterData) {
            commit('SET_FLOWMETER_NETWORK', payload);
        },
        updateFormStepData({ commit }, stepData: FormStepData) {
            commit('SET_FORM_STEP_DATA', stepData);
        },
        updateFormFlowmetersData({ commit }, flowmetersData: FlowmetersFormData) {
            commit('SET_FORM_FLOWMETERS_DATA', flowmetersData);
        },
        updateFormProductsData({ commit }, productsData: ProductsFormData) {
            commit('SET_FORM_PRODUCTS_DATA', productsData);
        },
        updateFormAnalysesData({ commit }, analysesData: AnalysisFormData) {
            commit('SET_FORM_ANALYSES_DATA', analysesData);
        },
        initializeFormForEditing({ commit }, massBalanceData: MassBalanceConfig) {
            commit('POPULATE_FORM_FOR_EDITING', massBalanceData);
        },
        resetMassBalanceData({ commit }) {
            commit('RESET_MASS_BALANCE_DATA');
        },
        setE4Config({ commit }, config: E4Config) {
            commit('SET_E4_CONFIG', config);
        },
        setAnalysis({ commit }, analysis: AnalysisFormData) {
            commit('SET_ANALYSIS', analysis);
        },
    },
};

export default massBalanceModule;
